<template>
  <div class="c-lead-generation-form-block">
    <div class="c-lead-generation-form-block__text">
      <DsTag
          v-if="vm.baseViewModel.tag"
          :text="vm.baseViewModel.tag"
          class="c-lead-generation-form-block__tag"
      />

      <DsText
          v-if="vm.baseViewModel.headline"
		  :id="vm.baseViewModel.headlineId"
          html-element="h2"
          text-type="heading-3xl"
          class="c-lead-generation-form-block__headline"
      >
        {{ vm.baseViewModel.headline }}
      </DsText>

      <DsText
          v-if="vm.baseViewModel.description"
          html-element="div"
          text-type="body-md"
          text-style="regular"
          class="c-lead-generation-form-block__description"
      >
        <div v-html="vm.baseViewModel.description" />
      </DsText>

      <div
          v-if="vm.baseViewModel.uspList.length"
          class="c-lead-generation-form-block__list"
      >
        <DsText
            v-for="(item, index) in vm.baseViewModel.uspList"
            :key="index"
            html-element="div"
            text-type="body-md"
            class="c-lead-generation-form-block__list-item"
        >
          <DsIcon v-if="item.iconName" :icon-name="item.iconName" />
          {{ item.text }}
        </DsText>
      </div>
    </div>

    <div
        class="c-lead-generation-form-block__form"
        :class="{'c-lead-generation-form-block__form--bg-reverse': reverse}"
    >
      <FormKit
          v-if="submitStatus === null"
          id="lead-generation-form-block"
          type="form"
          :actions="false"
          :submit-attrs="{ input: false }"
          :config="{ validationVisibility: 'submit' }"
          @submit="submitHandler"
      >
        <FormKitSchema :schema="schemaWithoutSubmit" />
        <FormKitMessages :sections-schema="{ messages: { $el: null, children: [] }}" />

        <DsButton
            class="c-lead-generation-form-block__button"
            type="submit"
            variant="primary"
            size="md"
            :state="isSubmitting ? 'loading' : 'default'"
            :label-loading="submitButtonLabel"
        >
          {{ submitButtonLabel }}
        </DsButton>
      </FormKit>

      <!-- Success -->
      <div v-else-if="submitStatus === 'success'" class="c-lead-generation-form-block__status">
        <DsIcon
            :icon-name="vm.baseViewModel.successPopup.icon"
            class="c-lead-generation-form-block__status-icon"
        />
        <DsText
            html-element="div"
            text-type="heading-lg"
            text-style="regular"
            class="c-lead-generation-form-block__status-message"
        >
          {{ vm.baseViewModel.successPopup.message }}
        </DsText>
        <DsButton
            v-if="vm.baseViewModel.successPopup.button"
            variant="primary"
            size="md"
            :href="vm.baseViewModel.successPopup.button.url"
            :target="vm.baseViewModel.successPopup.button.target"
        >
          {{ vm.baseViewModel.successPopup.button.text }}
        </DsButton>
      </div>

      <!-- Error -->
      <div v-else-if="submitStatus === 'error'" class="c-lead-generation-form-block__status">
        <DsIcon
            :icon-name="vm.baseViewModel.errorPopup.icon"
            class="c-lead-generation-form-block__status-icon"
        />
        <DsText
            html-element="div"
            text-type="heading-lg"
            text-style="regular"
            class="c-lead-generation-form-block__status-message"
        >
          {{ vm.baseViewModel.errorPopup.message }}
        </DsText>
        <DsButton
            v-if="vm.baseViewModel.errorPopup.button"
            variant="primary"
            size="md"
            :href="vm.baseViewModel.errorPopup.button.url"
            :target="vm.baseViewModel.errorPopup.button.target"
        >
          {{ vm.baseViewModel.errorPopup.button.text }}
        </DsButton>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { DsTag, DsText, DsIcon, DsButton } from '@coloplast/design-system';
import { FormKit, FormKitSchema, FormKitMessages } from '@formkit/vue';
import { formatApiMessages } from "multisite/services/checkoutHelperService";
import axios from 'axios';

const props = defineProps({
  vm: {
    type: Object,
    default: () => ({}),
  }
});

const reverse = computed(() => props.vm.baseViewModel.backgroundColor === 'default');
const submitStatus = ref(null);
const isSubmitting = ref(false);

function stripAsteriskFromNode(node) {
  if (Array.isArray(node)) {
    return node.map(stripAsteriskFromNode);
  }
  if (node && typeof node === 'object') {
    const out = { ...node };

    if (out.sectionsSchema?.label?.children) {
      out.sectionsSchema = {
        ...out.sectionsSchema,
        label: {
          ...out.sectionsSchema.label,
          children: out.sectionsSchema.label.children.filter((c) => c !== '*'),
        },
      };
    }

    if (out.$formkit === 'checkbox') {
      out.config = {
        ...(out.config || {}),
        outerClass: `${out.config?.outerClass || ''} c-lead-generation-form-block__checkbox`.trim(),
      };
    }

    if (out.children) {
      out.children = stripAsteriskFromNode(out.children);
    }

    return out;
  }
  return node;
}

const schemaWithoutSubmit = computed(() => {
  const base = props.vm.baseViewModel.formkitSchema
      .filter((field) => field.$formkit !== 'spinningSubmit');
  return stripAsteriskFromNode(base);
});

const submitButtonLabel = computed(() => {
  const submitField = props.vm.baseViewModel.formkitSchema.find(f => f.$formkit === "spinningSubmit");
  return submitField?.label || 'Submit';
});

async function submitHandler(formValues, node) {
  isSubmitting.value = true;
  submitStatus.value = null;
  const submitUrl = props.vm.baseViewModel.apiUrl;

  try {
    const { status } = await axios.post(submitUrl, formValues);
    submitStatus.value = (status === 200 || status === 302) ? 'success' : 'error';
  } catch (error) {
    const { response } = error;
    const { data, status } = response || {};

    if (status === 400 && data?.message) {
      const errorObj = {};
      const apiToFormkitMap = {
        MobilePhone: 'mobilePhone_1',
        FirstName: 'firstName',
        LastName: 'lastName',
        Email: 'email'
      };

      formatApiMessages(data.message).forEach(({ name, message }) => {
        const fieldName = apiToFormkitMap[name] || name;
        errorObj[fieldName] = message;
      });

      node.setErrors([], errorObj);
    } else {
      submitStatus.value = 'error';
    }
  } finally {
    isSubmitting.value = false;
  }
}
</script>

<style lang="scss" scoped>
@import 'multisite/styles/settings/settings';
@import 'multisite/styles/tools/tools';

.c-lead-generation-form-block {
  // layout
  --grid-cols: 2;
  --text-cols: 2;
  --form-cols: 2;

  @media (min-width: ds-breakpoint(lg)) {
    --grid-cols: 12;
    --text-cols: 4;
    --form-cols: 8;
  }

  display: grid;
  grid-template-columns: repeat(var(--grid-cols), 1fr);
  gap: ds-space(8);

  &__text {
    display: flex;
    flex-direction: column;
    grid-column: span var(--text-cols);
    row-gap: ds-gap(6);
  }

  &__form {
    display: flex;
    flex-direction: column;
    grid-column: span var(--form-cols);
    margin: 0;
    padding: ds-space(8);
    border-radius: ds-border-radius(md);
    background: #FFFFFF;

    &--bg-reverse {
      background: var(--color-bg-block-neutral, #F7F8F8);

      .c-lead-generation-form-block__status-icon {
        background: #FFFFFF;
      }
    }

    @media (min-width: ds-breakpoint(md)) and (max-width: ds-breakpoint(lg)) {
      padding: ds-space(9);
    }
    @media (min-width: ds-breakpoint(lg)) {
      padding: ds-space(12);
    }
  }

  &__headline,
  &__description {
    margin: 0;
  }

  &__headline {
    margin-inline-end: ds-space(8);
  }

  &__list {
    display: flex;
    flex-direction: column;
    margin: ds-gap(6) 0;
    gap: ds-gap(6);

    &-item {
      margin: 0;

      :deep {
        .ds-icon {
          margin-inline-end: ds-gap(6);
          color: ds-color('fg', 'brand');
        }
      }
    }
  }

  &__status {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ds-space(10);
    padding: ds-space(6) 0;

    &-icon {
      display: flex;
      justify-items: center;
      align-items: center;
      inline-size: 104px;
      block-size: 104px;
      background: ds-color(bg, decorative-3-muted);
      border-radius: 50%;
      color: ds-color(fg, brand);
      padding: ds-space(9);
    }

    &-message {
      text-align: center;
    }
  }

  :deep {
    .c-lead-generation-form-block__button {
      margin-block-start: ds-gap(6);
    }

    .c-lead-generation-form-block__checkbox {
      .formkit-inner {
        margin-inline-end: ds-gap(5);
      }

      .formkit-label {
        font-size: t-rem(16px);
      }

      .formkit-help {
        margin-block-start: 0;
        margin-inline-start: 38px;
      }

      .ds-text-body-md,
      .ds-text-label-md {
        font-size: t-rem(14px);
      }

      .ds-text-label-md {
        text-decoration: underline;
      }

      .ds-button {
        margin: 0;
      }

      div[data-type=richtext].closed>div {
        max-block-size: 42px;
      }
    }

    .formkit-form {
      display: flex;
      flex-direction: column;
      row-gap: ds-gap(7);
    }

    .formkit-outer {
      --fk-margin-outer: auto 0 0 0;
      
      padding: 0;
    }

    .phone-number {
      display: flex;
      gap: ds-gap(4);
      align-items: flex-start;

      & > div:first-child {
        min-inline-size: 110px;
        inline-size: 110px;

        @media (max-width: ds-breakpoint(sm)) {
          min-inline-size: auto;
          inline-size: 88px;
        }

        .formkit-placeholder,
        .formkit-selection-wrapper {
          inline-size: 68px;

          @media (max-width: ds-breakpoint(sm)) {
            inline-size: 46px;
          }
        }
      }

      & > div:last-child {
        flex-grow: 1;
      }
    }

    .formkit-selector {
      background-color: #FFFFFF;
      border-radius: ds-border-radius("md");
    }

    .rich {
      p {
        margin-block-start: 0;

        &:last-child {
          margin-block-end: 0;
        }
      }
    }
  }
}
</style>
